<script lang="ts">
    import { writable, type Readable, type Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'

    import { gettext as _ } from 'hydria/services/gettext'

    import { types } from 'wdom/zone/zone'
    import type { EditableRecord, Zone, ZoneTemplate } from 'wdom/zone/zone'

    import PopEntry from 'hydria/components/PopEntry.svelte'
    import HelpButton from 'wdom/components/HelpButton.svelte'
    import Record from './Record.svelte'

    let filterByType: Writable<string | undefined> = writable(undefined)
    let possibleFilterTypes: Writable<Set<string>> = writable(new Set())

    let filterByText: Writable<string | undefined> = writable(undefined)
    let filterByTextShown: boolean = $state(false)
    let filterByTextEdit: string = $state('')
    let filterByTextElement: HTMLInputElement | undefined = $state()

    function closeFilterByText() {
        filterByTextShown = false
    }
    function openFilterByText() {
        filterByTextEdit = $filterByText != undefined ? $filterByText : ''
        filterByTextShown = true
    }
    function setFilterByText(value: string) {
        value = value.trim()
        if (value == '') $filterByText = undefined
        else $filterByText = value
        closeFilterByText()
    }
    function clearFilterByText() {
        $filterByText = undefined
    }

    interface Props {
        status: Writable<'loading' | 'ready' | 'not-found' | 'error'>
        zone: Writable<Zone | ZoneTemplate | undefined>
        records: Writable<EditableRecord[]>
        busy: Writable<boolean | string>
        zoneUnicode: Readable<string | undefined>
        zoneEdit: Writable<Zone | ZoneTemplate | undefined>
        class?: string | null
        recordsChanged: () => void
        editRecord: (recPos: number) => void
    }

    let { status, zone, records, busy, zoneUnicode, zoneEdit, class: classes = null, recordsChanged, editRecord }: Props = $props()

    function updatePossibleTypes(filterByText: string | undefined, records: EditableRecord[]) {
        const etypes: Set<string> = new Set()
        records.map((record: EditableRecord) => {
            if (record.status == 'saved') {
                if (!isTextFiltered(filterByText, record)) etypes.add(record.saved.type)
            }
        })
        $possibleFilterTypes = etypes
        if ($filterByType && !etypes.has($filterByType)) $filterByType = undefined
    }
    $effect(() => {
        updatePossibleTypes($filterByText, $records)
    })

    function setTypeFilter(type: string | undefined) {
        $filterByType = type
    }

    function toggleTypeFilter(type: string) {
        $filterByType = $filterByType == type ? undefined : type
    }

    function isFiltered(filterByType: string | undefined, filterByText: string | undefined, record: EditableRecord) {
        if (record.status != 'saved') return false

        if (filterByType !== undefined && filterByType != record.saved.type) return true

        return isTextFiltered(filterByText, record)
    }
    function isTextFiltered(filterByText: string | undefined, record: EditableRecord) {
        if (filterByText === undefined) return false

        let cmpFields: Set<string> = new Set()
        switch (record.saved.type) {
            case 'A':
            case 'AAAA':
            case 'ANAME':
            case 'CNAME':
            case 'DNAME':
            case 'NS':
            case 'MX':
            case 'SRV':
                cmpFields.add('target')
                break
            case 'CAA':
                cmpFields.add('tag')
                cmpFields.add('value')
                break
            case 'REDIRECT':
            case 'STATICURL':
            case 'TXT':
                cmpFields.add('value')
                break
        }
        if (cmpFields.size < 1) return false

        for (const fld of Array.from(cmpFields)) {
            if (record.saved[fld] != null && (record.saved[fld] as string).toLowerCase().includes(filterByText)) return false
        }

        if (record.saved.idna.includes(filterByText)) return false
        return true
    }

    function remove(rg: EditableRecord) {
        const pos = $records.indexOf(rg)
        if (pos >= 0) {
            records.update((v) => {
                if (v) v.splice(pos, 1)
                return v
            })
        }
    }
</script>

<style>
    .zone-record-edit {
        --rr-spacing-x: 0.5rem;

        margin-left: calc(var(--rr-spacing-x) * -1);
        margin-right: calc(var(--rr-spacing-x) * -1);

        width: 100%;
    }

    :global(.rr) {
        display: grid;
        gap: 0 0.75rem;
        grid-template-columns: 1.75rem 2fr 3.5rem 5.25em 5fr 6.25em;
        grid-template-rows: auto;
        align-items: center;

        --bs-bg-opacity: 0.15;

        padding-left: var(--rr-spacing-x);
        padding-right: var(--rr-spacing-x);
    }
    :global(.rr):not(:last-child) {
        border-bottom: 1px solid #f3f5f6;
    }
    .rr-head.rr {
        border-bottom: 2px solid #f3f5f6;
        font-weight: 600;
    }

    :global(.rr-cell) {
        padding: 12px 0;
    }
    :global(.rr-cell):nth-child(1) {
        text-align: center;
    }
    :global(.rr-cell):nth-child(2) {
        text-align: right;
    }
    :global(.rr-cell):nth-child(3) {
        text-align: right;
    }
    :global(.rr-cell):nth-child(4) {
        text-align: center;
    }

    :global(.rr-cell):nth-child(6) {
        justify-self: end;
    }

    .filter-types {
        white-space: wrap;
        flex-wrap: wrap;
        align-items: baseline;
        justify-content: center;
        display: flex;
        gap: 0.25rem 0.25rem;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape' && filterByTextShown) closeFilterByText()
        else if (e.key == 'Enter' && filterByTextShown) setFilterByText(filterByTextEdit)
    }}
/>

{#if $status == 'ready'}
    <div>
        <div class="standalone-title text-primary d-flex justify-content-between gap-4">
            <span>{$_('Records')}</span>
            <span class="filter-types">
                {#if $records && $records.length > 0 && $filterByText === undefined}
                    <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => openFilterByText()} data-tooltip={$_('Filtrer par texte')}><Icon inline icon="mdi:text-search-variant" /></button>
                {:else if $records && $records.length > 0}
                    <div class="btn-group btn-group-sm">
                        <button type="button" class="btn btn-sm btn-secondary" onclick={() => openFilterByText()}><Icon inline icon="mdi:text-search-variant" /> {$filterByText}</button>
                        <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => clearFilterByText()} data-tooltip={$_('Ne plus filtrer')}><Icon inline icon="mdi:times" /></button>
                    </div>
                {/if}
                {#if filterByTextShown}
                    <PopEntry
                        close={() => close()}
                        mounted={() => {
                            if (filterByTextElement) {
                                filterByTextElement.focus()
                                filterByTextElement.select()
                            }
                        }}
                    >
                        <form
                            onsubmit={(e) => {
                                e.preventDefault()
                                setFilterByText(filterByTextEdit)
                            }}
                        >
                            <div class="card">
                                <div class="card-header text-bg-primary">{$_('Filtrer par texte')}</div>
                                <div class="card-body">
                                    <input type="text" class="form-control" bind:value={filterByTextEdit} bind:this={filterByTextElement} />
                                </div>
                                <div class="card-footer d-flex gap-2 justify-content-end">
                                    <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => closeFilterByText()}>{$_('Fermer')}</button>
                                    <button type="submit" class="btn btn-sm btn-primary">{$_('Appliquer')}</button>
                                </div>
                            </div>
                        </form>
                    </PopEntry>
                {/if}
                {#if $possibleFilterTypes.size > 1}
                    <!-- <div class="filter-group"> -->
                    {#each types as ftype}
                        {#if $possibleFilterTypes.has(ftype)}
                            <button type="button" class="btn btn-sm btn-outline-secondary" class:active={$filterByType === ftype} onclick={() => toggleTypeFilter(ftype)}>{ftype}</button><!--data-tooltip={$resolve(typeDescriptions[ftype])}-->
                        {/if}
                    {/each}
                    {#if $filterByType !== undefined}
                        <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => setTypeFilter(undefined)} data-tooltip={$_('Ne plus filtrer')}><Icon inline icon="mdi:times" /></button>
                    {/if}
                    <!-- </div> -->
                {/if}
            </span>
            <HelpButton tag="zone-entries" />
        </div>
        <div class="card zone{classes ? ` ${classes}` : ''}">
            <div class="card-body">
                {#if !$records.length}
                    <div class="text-danger">{$_('No records')}</div>
                {:else}
                    <div class="table-responsive">
                        <div class="zone-record-edit">
                            <div class="rr rr-head">
                                <div class="rr-cell"></div>
                                <div class="rr-cell">{$_('Entry name')}</div>
                                <div class="rr-cell">{$_('TTL')}</div>
                                <div class="rr-cell">{$_('Type')}</div>
                                <div class="rr-cell">{$_('Value')}</div>
                                <div class="rr-cell">{$_('Actions')}</div>
                            </div>
                            {#each $records as rgroup, i}
                                {#if !isFiltered($filterByType, $filterByText, rgroup)}
                                    <!-- <Record rg={rgroup} removeRecord={(rg) => remove(rg)} editRecord={(rg) => edit(rg)} {busy} {zoneUnicode} {zoneEdit} {status} {recordsChanged} /> -->
                                    <Record
                                        bind:rg={$records[i]}
                                        removeRecord={(rg) => remove(rg)}
                                        editRecord={() => {
                                            editRecord(i)
                                        }}
                                        {busy}
                                        {zoneUnicode}
                                        {zoneEdit}
                                        {status}
                                        {recordsChanged}
                                    />
                                {/if}
                            {/each}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </div>
{/if}
